import React, { useMemo, useState } from 'react';

import { UserOutlined } from '@ant-design/icons';
import { MultipleValue } from '@shared/table/Rules/component';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { Tag, Space, Button, Popconfirm, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import Can from '@shared/permissions/Can';

import { formatDateTime } from '../../../utils/dateTime';

interface OperatorService extends TableService {
  enable: (id: string) => Promise<{ success: boolean; message?: string }>;
  disable: (id: string) => Promise<{ success: boolean; message?: string }>;
}

interface OperatorRow {
  id: string;
  name?: string;
  username?: string;
  email?: string;
  disabled_at?: string | null;
  created_at?: string;
  editable?: boolean;
  [key: string]: unknown;
}

interface OperatorsListProps {
  role?: string;
  service: OperatorService;
}

const OperatorsList: React.FC<OperatorsListProps> = ({
  role = 'operator',
  service,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { operator: currentOperator } = useOperatorAuth();
  const [lastModified, setLastModified] = useState(0);

  const tableName = 'operators';

  const handleToggleStatus = async (record: OperatorRow) => {
    try {
      const result = record.disabled_at
        ? await service.enable(record.id)
        : await service.disable(record.id);
      if (result.success) {
        message.success(t(record.disabled_at ? 'common.message.enable_success' : 'common.message.disable_success'));
        setLastModified(Date.now());
      } else {
        message.error(result.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      message.error(t('common.message.operation_failed'));
    }
  };

  const columns = useMemo<unknown[]>(() => {
    const cols: unknown[] = [
      {
        nameKey: `operator.pages.operators.list.columns.name`,
        sortable: true,
        sortFieldName: `${tableName}.name`,
        wrap: true,
        width: '15%',
        cell: ({ name }: OperatorRow) => (
          <Space>
            <UserOutlined />
            {name || '-'}
          </Space>
        ),
      },
      {
        nameKey: `operator.pages.operators.list.columns.username`,
        sortable: true,
        sortFieldName: `${tableName}.username`,
        wrap: true,
        width: '15%',
        selector: ({ username }: OperatorRow) => username,
      },
      {
        nameKey: `operator.pages.operators.list.columns.email`,
        sortable: true,
        sortFieldName: `${tableName}.email`,
        wrap: true,
        width: '20%',
        selector: ({ email }: OperatorRow) => email || '-',
      },
      {
        nameKey: 'common.status_label',
        sortable: false,
        width: '8%',
        cell: ({ disabled_at }: OperatorRow) => (
          <Tag color={disabled_at ? 'red' : 'green'}>
            {t(disabled_at ? 'enums.operator.status.deactivated' : 'enums.operator.status.activated')}
          </Tag>
        ),
      },
      {
        nameKey: `operator.pages.operators.list.columns.created_at`,
        sortable: true,
        sortFieldName: `${tableName}.created_at`,
        width: '12%',
        selector: ({ created_at }: OperatorRow) => created_at ? formatDateTime(created_at) : '-',
      },
      {
        nameKey: 'common.actions',
        persist: true,
        sortable: false,
        cell: (record: OperatorRow) => {
          // 自編跟編別人共用同一個 Edit 頁；只有「停用自己」這條禁止
          const isSelf = record.id === currentOperator?.id;
          return (
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} size={4}>
              {record.editable && (
                <Can requiredPermission="operators:write">
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, minWidth: 32 }}
                    onClick={() => navigate(`/operators/${record.id}/edit`)}
                  >
                    {t('common.button.edit')}
                  </Button>
                </Can>
              )}
              <Can requiredPermission="operators:write">
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => navigate(`/operators/${record.id}/merchants`)}
                >
                  {t(`operator.pages.operators.list.button.manage_merchants`)}
                </Button>
              </Can>
              {!isSelf && (
                <Can requiredPermission="operators:write">
                  <Popconfirm
                    title={t(record.disabled_at ? 'common.message.confirm_enable' : 'common.message.confirm_disable')}
                    onConfirm={() => handleToggleStatus(record)}
                    okText={t('common.button.confirm')}
                    cancelText={t('common.button.cancel')}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger={!record.disabled_at}
                      style={{ padding: 0 }}
                    >
                      {t(record.disabled_at ? 'common.button.enable' : 'common.button.disable')}
                    </Button>
                  </Popconfirm>
                </Can>
              )}
            </Space>
          );
        },
      },
    ];

    return cols;
  }, [role, tableName, t, navigate, currentOperator]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('operator.pages.operators.list.filters.status'),
      component: MultipleValue,
      inputName: 'operators.status',
      defaultPinned: true,
      defaultValue: ['active'],
      createable: false,
      defaultOptions: [
        { label: t('enums.operator.status.activated'), value: 'active' },
        { label: t('enums.operator.status.deactivated'), value: 'disabled' },
      ],
    },
  ], [t]);

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('operator.pages.operators.list.columns.name'),
          t('operator.pages.operators.list.columns.username'),
          t('operator.pages.operators.list.columns.email'),
        ],
      }}
      columnsEditable={true}
      tableId="OperatorAccountsTable"
      defaultQuery={{
        sorting_field: 'operators.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      service={service}
      lastModified={lastModified}
    />
  );
};

export default OperatorsList;
